// src/context/AppContext.tsx

import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { Product, CartItem, AppState as ImportedAppState } from '../types';
import { productsApi } from '../utils/api';

type AppAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'TOGGLE_WISHLIST'; payload: Product }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_PAGE'; payload: 'home' | 'shop' | 'product' | 'about' | 'contact' }
  | { type: 'SET_SELECTED_PRODUCT'; payload: number }
  | { type: 'SET_PRODUCTS'; payload: Product[] }
  | { type: 'SET_CART'; payload: (Product & { quantity: number; selectedVariations?: any })[] }
  | { type: 'REFRESH_PRODUCTS' };

export interface AppState {
  cart: CartItem[];
  wishlist: Product[];
  searchQuery: string;
  currentPage: 'home' | 'shop' | 'product' | 'about' | 'contact' | 'checkout';
  selectedProductId: number | null;
  products: Product[];
  lastRefresh: number;
}

const initialState: AppState = {
  cart: [],
  wishlist: [],
  searchQuery: '',
  currentPage: 'home',
  selectedProductId: null,
  products: [],
  lastRefresh: 0
};

// Load state from localStorage
const loadState = (): AppState => {
  try {
    const serializedState = localStorage.getItem('appState');
    if (serializedState === null) {
      return initialState;
    }
    const parsedState = JSON.parse(serializedState);
    return {
      ...initialState,
      ...parsedState,
      products: [], // Don't load products from localStorage
      lastRefresh: parsedState.lastRefresh || 0
    };
  } catch (err) {
    return initialState;
  }
};

const saveState = (state: AppState) => {
  try {
    // Don't save products to localStorage
    const { products, ...stateToSave } = state;
    const serializedState = JSON.stringify(stateToSave);
    localStorage.setItem('appState', serializedState);
  } catch (err) {
    // Handle errors here
  }
};

const computeItemKey = (item: CartItem) => {
    return `${item.id}${item.selectedVariations ? '-' + JSON.stringify(item.selectedVariations) : ''}`;
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  let newState: AppState;

  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.cart.findIndex(
        item =>
          item.id === action.payload.id &&
          JSON.stringify(item.selectedVariations) === JSON.stringify(action.payload.selectedVariations)
      );

      if (existingItemIndex !== -1) {
        return {
          ...state,
          cart: state.cart.map((item, index) =>
            index === existingItemIndex
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          ),
        };
      }
      return {
        ...state,
        cart: [...state.cart, action.payload],
      };
    }

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        cart: state.cart.map(item =>
          computeItemKey(item) === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cart: state.cart.filter(item => computeItemKey(item) !== action.payload),
      };

    case 'TOGGLE_WISHLIST': {
      const exists = state.wishlist.some(item => item.id === action.payload.id);
      return {
        ...state,
        wishlist: exists
          ? state.wishlist.filter(item => item.id !== action.payload.id)
          : [...state.wishlist, action.payload],
      };
    }

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload,
      };

    case 'SET_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };

    case 'SET_SELECTED_PRODUCT':
      return {
        ...state,
        selectedProductId: action.payload,
        currentPage: 'product',
      };

    case 'SET_PRODUCTS':
      newState = {
        ...state,
        products: action.payload,
        lastRefresh: Date.now()
      };
      break;

    case 'SET_CART':
      return {
        ...state,
        cart: action.payload,
      };

    case 'REFRESH_PRODUCTS':
      newState = {
        ...state,
        lastRefresh: Date.now()
      };
      break;

    default:
      return state;
  }

  saveState(newState);
  return newState;
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  isLoading: boolean;
  error: string | null;
} | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, loadState());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch products
  const fetchProducts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await productsApi.getAll();
      dispatch({ type: 'SET_PRODUCTS', payload: response.data });
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchProducts();

    // Set up periodic refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchProducts();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch, isLoading, error }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
